(function () {
  'use strict';

  // Storefront-only switch; retain product associations and Admin editing.
  var SHOPPABLE_LINKS_ENABLED = false;

  var ROOT_SELECTOR = '[data-phase5-native-gallery]';
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function text(value) {
    return value == null ? '' : String(value);
  }

  function setText(element, value) {
    if (element) element.textContent = text(value);
  }

  function createElement(tag, className, content) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (content != null) element.textContent = text(content);
    return element;
  }

  function safeDate(value) {
    if (!value) return '';
    var date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    try {
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return date.toLocaleDateString();
    }
  }

  function isSafeHttpUrl(value) {
    if (!value) return false;
    try {
      var parsed = new URL(value, window.location.origin);
      return parsed.protocol === 'https:' || (parsed.protocol === 'http:' && parsed.hostname === 'localhost');
    } catch (error) {
      return false;
    }
  }

  function normalizeMedia(media) {
    if (!media || typeof media !== 'object') return null;

    var kind = media.kind === 'video' ? 'video' : 'image';
    var url = media.url || media.servingUrl || media.src || '';
    var poster = media.poster || media.posterUrl || '';

    if (!url || !isSafeHttpUrl(url)) return null;

    return {
      key: text(media.key),
      kind: kind,
      role: text(media.role || 'content'),
      url: url,
      poster: isSafeHttpUrl(poster) ? poster : '',
      alt: text(media.alt)
    };
  }

  function normalizeProduct(product) {
    if (!product || typeof product !== 'object') return null;
    var url = product.url || '';
    if (!url) return null;

    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.origin !== window.location.origin && parsed.protocol !== 'https:') return null;
      url = parsed.origin === window.location.origin ? parsed.pathname + parsed.search + parsed.hash : parsed.href;
    } catch (error) {
      return null;
    }

    return {
      url: url,
      title: text(product.title || 'View product'),
      shopifyProductId: text(product.shopifyProductId || product.shopify_product_id || product.productId)
    };
  }

  function normalizeItem(item) {
    if (!item || typeof item !== 'object') return null;

    var media = Array.isArray(item.media) ? item.media.map(normalizeMedia).filter(Boolean) : [];
    if (!media.length) return null;

    var products = Array.isArray(item.productLinks)
      ? item.productLinks.map(normalizeProduct).filter(Boolean)
      : [];

    return {
      id: text(item.id),
      type: text(item.type || (media.length > 1 ? 'carousel' : media[0].kind)),
      source: text(item.source || 'instagram'),
      username: text(item.username || 'Phase Five'),
      caption: text(item.caption),
      permalink: isSafeHttpUrl(item.permalink) ? item.permalink : '',
      createdAt: text(item.createdAt || item.created_at),
      media: media,
      productLinks: products
    };
  }

  function galleryMedia(item) {
    var content = item.media.filter(function (media) { return media.role === 'content'; });
    if (!content.length) {
      content = item.media.filter(function (media) { return media.role !== 'poster' && media.role !== 'cover'; });
    }
    if (!content.length) content = item.media.slice(0, 1);

    var posterRecord = item.media.find(function (media) { return media.role === 'poster'; });
    return content.map(function (media) {
      if (media.kind === 'video' && !media.poster && posterRecord) {
        return Object.assign({}, media, { poster: posterRecord.url });
      }
      return media;
    });
  }

  function cardMedia(item) {
    var cover = item.media.find(function (media) { return media.role === 'cover' && media.kind === 'image'; });
    if (cover) return cover;

    var content = galleryMedia(item)[0];
    if (content && content.kind === 'video' && content.poster) {
      return { kind: 'image', url: content.poster, alt: content.alt, role: 'poster' };
    }
    return content;
  }

  function NativeGallery(root) {
    this.root = root;
    this.shell = root.closest('[data-product-gallery-shell]');
    this.grid = root.querySelector('[data-gallery-grid]');
    this.status = root.querySelector('[data-gallery-status]');
    this.skeleton = root.querySelector('[data-gallery-skeleton]');
    this.loadMoreButton = root.querySelector('[data-gallery-load-more]');
    this.carouselMode = root.dataset.galleryMode === 'carousel';
    this.carouselViewport = root.querySelector('[data-gallery-carousel-viewport]');
    this.carouselPreviousButton = root.querySelector('[data-gallery-carousel-previous]');
    this.carouselNextButton = root.querySelector('[data-gallery-carousel-next]');
    this.modal = root.querySelector('[data-gallery-modal]');
    this.mediaPane = root.querySelector('[data-gallery-media]');
    this.mediaContent = root.querySelector('[data-gallery-media-content]');
    this.caption = root.querySelector('[data-gallery-caption]');
    this.byline = root.querySelector('[data-gallery-byline]');
    this.products = root.querySelector('[data-gallery-products]');
    this.instagramLink = root.querySelector('[data-gallery-instagram]');
    this.mediaPagination = root.querySelector('[data-gallery-media-pagination]');
    this.mediaCount = root.querySelector('[data-gallery-media-count]');
    this.mediaDots = root.querySelector('[data-gallery-media-dots]');
    this.previousItemButton = root.querySelector('[data-gallery-previous-item]');
    this.nextItemButton = root.querySelector('[data-gallery-next-item]');
    this.uploadButton = root.querySelector('[data-gallery-upload-open]');
    this.uploadDialog = root.querySelector('[data-gallery-upload-dialog]');
    this.uploadForm = root.querySelector('[data-gallery-upload-form]');
    this.uploadStatus = root.querySelector('[data-gallery-upload-status]');
    this.pageSize = Math.max(1, parseInt(root.dataset.pageSize || '12', 10));
    this.manifestUrl = root.dataset.manifestUrl || '';
    this.productMapUrl = root.dataset.productMapUrl || '';
    this.productManifestBaseUrl = root.dataset.productManifestBaseUrl || '';
    this.productHandle = root.dataset.productHandle || '';
    this.persistentProductGallery = root.dataset.productGalleryPersistent === 'true';
    this.productFallbackHandle = root.dataset.productFallbackHandle || '';
    this.uploadEndpoint = root.dataset.uploadEndpoint || '';
    this.turnstileSiteKey = root.dataset.turnstileSiteKey || '';
    this.items = [];
    this.rendered = 0;
    this.nextUrl = '';
    this.activeItemIndex = -1;
    this.activeMediaIndex = 0;
    this.openDialog = null;
    this.returnFocus = null;
    this.loading = false;
    this.mediaPointerStart = null;

    this.onRootClick = this.onRootClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onUploadSubmit = this.onUploadSubmit.bind(this);
    this.updateCarouselControls = this.updateCarouselControls.bind(this);
    this.onMediaPointerDown = this.onMediaPointerDown.bind(this);
    this.onMediaPointerUp = this.onMediaPointerUp.bind(this);
    this.onMediaPointerCancel = this.onMediaPointerCancel.bind(this);

    this.root.addEventListener('click', this.onRootClick);
    document.addEventListener('keydown', this.onKeydown);
    if (this.mediaPane) {
      this.mediaPane.addEventListener('pointerdown', this.onMediaPointerDown);
      this.mediaPane.addEventListener('pointerup', this.onMediaPointerUp);
      this.mediaPane.addEventListener('pointercancel', this.onMediaPointerCancel);
    }
    if (this.uploadForm) this.uploadForm.addEventListener('submit', this.onUploadSubmit);
    if (this.carouselViewport) {
      this.carouselViewport.addEventListener('scroll', this.updateCarouselControls, { passive: true });
      window.addEventListener('resize', this.updateCarouselControls);
    }

    this.start();
  }

  NativeGallery.prototype.start = function () {
    var embedded = this.root.querySelector('[data-gallery-data]');
    var embeddedPayload = null;

    if (embedded) {
      try {
        embeddedPayload = JSON.parse(embedded.textContent);
      } catch (error) {
        this.showStatus(this.root.dataset.errorMessage || 'The gallery could not be loaded.', true);
      }
    }

    if (this.manifestUrl && isSafeHttpUrl(this.manifestUrl)) {
      this.fetchPage(this.manifestUrl, true);
      return;
    }

    if (
      this.productHandle
      && isSafeHttpUrl(this.productMapUrl)
      && isSafeHttpUrl(this.productManifestBaseUrl)
    ) {
      var gallery = this;
      fetch(this.productMapUrl, { credentials: 'omit', mode: 'cors', cache: 'no-store' })
        .then(function (response) {
          if (!response.ok) throw new Error('Product gallery map returned ' + response.status);
          return response.json();
        })
        .then(function (payload) {
          var products = payload && payload.products;
          var galleryKey = products ? text(products[gallery.productHandle] || products[gallery.productFallbackHandle]) : '';
          if (!/^[a-z0-9-]+$/.test(galleryKey)) throw new Error('Product does not have a gallery mapping.');
          var base = gallery.productManifestBaseUrl.replace(/\/?$/, '/');
          var manifestUrl = new URL(encodeURIComponent(galleryKey) + '.json', base).href;
          if (!isSafeHttpUrl(manifestUrl)) throw new Error('Product gallery manifest URL is invalid.');
          gallery.manifestUrl = manifestUrl;
          gallery.fetchPage(manifestUrl, true);
        })
        .catch(function () {
          gallery.hideSkeleton();
          if (gallery.shell) gallery.shell.hidden = !gallery.persistentProductGallery;
          if (gallery.persistentProductGallery) gallery.showStatus(gallery.root.dataset.emptyMessage || 'Be the first to share a ride on this board.', true);
        });
      return;
    }

    if (embeddedPayload) {
      this.consumePayload(embeddedPayload, true);
      return;
    }

    this.hideSkeleton();
    this.showStatus(this.root.dataset.emptyMessage || 'No gallery posts are available yet.', true);
  };

  NativeGallery.prototype.consumePayload = function (payload, replace) {
    if (payload && payload.revision) this.root.dataset.galleryRevision = text(payload.revision);
    if (payload && payload.gallery) this.root.dataset.galleryKey = text(payload.gallery.key);
    var rawItems = Array.isArray(payload) ? payload : (Array.isArray(payload.items) ? payload.items : []);
    var normalized = rawItems.map(normalizeItem).filter(Boolean);

    if (replace) {
      this.items = normalized;
      this.rendered = 0;
      this.grid.replaceChildren();
    } else {
      var knownIds = new Set(this.items.map(function (item) { return item.id; }));
      this.items = this.items.concat(normalized.filter(function (item) { return !knownIds.has(item.id); }));
    }

    this.nextUrl = Array.isArray(payload) ? '' : text(payload.next || payload.nextPage || payload.next_url);
    if (this.nextUrl && !isSafeHttpUrl(this.nextUrl)) this.nextUrl = '';
    this.hideSkeleton();

    if (!this.items.length) {
      if (this.shell && this.persistentProductGallery) this.shell.hidden = false;
      this.showStatus(this.root.dataset.emptyMessage || 'No gallery posts are available yet.', true);
      this.updateLoadMore();
      return;
    }

    if (this.shell) {
      var productHeading = this.shell.querySelector('[data-product-gallery-heading]');
      if (productHeading && productHeading.dataset.shapeHashtag) {
        setText(productHeading, productHeading.dataset.shapeHashtag);
      } else if (productHeading && payload && (payload.displayName || (payload.gallery && payload.gallery.displayName))) {
        var prefix = text(productHeading.dataset.headingPrefix || '#P5');
        var name = text(payload.displayName || payload.gallery.displayName).toUpperCase().replace(/[^A-Z0-9]/g, '');
        setText(productHeading, prefix + name);
      }
      this.shell.hidden = false;
    }

    this.showStatus('', false);
    this.renderMore();
  };

  // The archive URLs remain valid rollback references in existing theme settings.
  // Resolve only this brand's known archive paths to the Admin-published feed.
  NativeGallery.prototype.publishedFeedUrl = function (url) {
    var parsed = new URL(url, window.location.origin);
    if (parsed.origin !== 'https://phase5-ugc-dev.phase5-ugc-service.workers.dev') return url;
    var main = parsed.pathname.match(/^\/manifests\/main\/page-(\d+)\.json$/);
    var product = parsed.pathname.match(/^\/manifests\/product\/([a-z0-9-]+)\.json$/);
    if (main) parsed.pathname = '/gallery/v2/galleries/main/pages/' + Number(main[1]);
    else if (product) parsed.pathname = '/gallery/v2/galleries/' + product[1] + '/pages/1';
    else return url;
    return parsed.href;
  };

  NativeGallery.prototype.fetchPage = function (url, replace) {
    var gallery = this;
    if (this.loading) return;

    this.loading = true;
    if (this.loadMoreButton) {
      this.loadMoreButton.disabled = true;
      this.loadMoreButton.setAttribute('aria-busy', 'true');
    }

    url = this.publishedFeedUrl(url);
    this.root.dataset.galleryFeedUrl = url;
    fetch(url, { credentials: 'omit', mode: 'cors', cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('Gallery manifest returned ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        gallery.consumePayload(payload, replace);
      })
      .catch(function () {
        gallery.hideSkeleton();
        gallery.showStatus(gallery.root.dataset.errorMessage || 'The gallery could not be loaded. Please try again.', true);
      })
      .finally(function () {
        gallery.loading = false;
        if (gallery.loadMoreButton) {
          gallery.loadMoreButton.disabled = false;
          gallery.loadMoreButton.removeAttribute('aria-busy');
        }
        gallery.updateLoadMore();
      });
  };

  NativeGallery.prototype.renderMore = function () {
    var end = this.carouselMode
      ? this.items.length
      : Math.min(this.rendered + this.pageSize, this.items.length);
    var fragment = document.createDocumentFragment();

    for (var index = this.rendered; index < end; index += 1) {
      fragment.appendChild(this.createCard(this.items[index], index));
    }

    this.grid.appendChild(fragment);
    this.rendered = end;
    this.updateLoadMore();
    if (this.carouselMode) window.requestAnimationFrame(this.updateCarouselControls);
  };

  NativeGallery.prototype.createCard = function (item, index) {
    var listItem = createElement('li', 'phase5-native-gallery__item');
    var button = createElement('button', 'phase5-native-gallery__card');
    var preview = cardMedia(item);
    var captionSummary = item.caption.replace(/\s+/g, ' ').trim();
    var accessibleName = captionSummary || ('Gallery post by ' + item.username);
    var actionLabel = createElement(
      'span',
      'phase5-native-gallery__card-label',
      SHOPPABLE_LINKS_ENABLED && item.productLinks.length ? 'SHOP THIS LOOK' : 'SEE MORE'
    );

    button.type = 'button';
    button.dataset.galleryItemIndex = String(index);
    button.setAttribute('aria-label', accessibleName.slice(0, 160) + '. Open details.');

    var image = createElement('img', 'phase5-native-gallery__image');
    image.src = preview.url;
    image.alt = preview.alt || captionSummary.slice(0, 140);
    image.width = 640;
    image.height = 640;
    image.decoding = 'async';
    if (index < 4) {
      image.loading = 'eager';
      image.fetchPriority = 'high';
    } else {
      image.loading = 'lazy';
    }

    button.appendChild(image);
    button.appendChild(actionLabel);
    listItem.appendChild(button);
    return listItem;
  };

  NativeGallery.prototype.onRootClick = function (event) {
    var itemButton = event.target.closest('[data-gallery-item-index]');
    if (itemButton && this.root.contains(itemButton)) {
      this.openItem(parseInt(itemButton.dataset.galleryItemIndex, 10), itemButton);
      return;
    }

    if (event.target.closest('[data-gallery-load-more]')) {
      if (this.rendered < this.items.length) this.renderMore();
      else if (this.nextUrl) this.fetchPage(this.nextUrl, false);
      return;
    }

    if (event.target.closest('[data-gallery-carousel-previous]')) {
      this.scrollCarousel(-1);
      return;
    }

    if (event.target.closest('[data-gallery-carousel-next]')) {
      this.scrollCarousel(1);
      return;
    }

    if (event.target.closest('[data-gallery-close]')) {
      this.closeActiveDialog();
      return;
    }

    if (event.target.closest('[data-gallery-previous-item]')) {
      this.changeItem(-1);
      return;
    }

    if (event.target.closest('[data-gallery-next-item]')) {
      this.changeItem(1);
      return;
    }

    var mediaDot = event.target.closest('[data-gallery-media-index]');
    if (mediaDot && this.root.contains(mediaDot)) {
      this.changeMedia(parseInt(mediaDot.dataset.galleryMediaIndex, 10), true);
      return;
    }

    if (event.target.closest('[data-gallery-upload-open]')) {
      this.openUpload(event.target.closest('[data-gallery-upload-open]'));
      return;
    }

    if ((event.target === this.modal || event.target === this.uploadDialog) && this.openDialog) {
      this.closeActiveDialog();
    }
  };

  NativeGallery.prototype.openItem = function (index, source) {
    if (!Number.isInteger(index) || !this.items[index]) return;
    this.activeItemIndex = index;
    this.activeMediaIndex = 0;
    this.renderModal();
    this.openModal(this.modal, source);
  };

  NativeGallery.prototype.renderModal = function () {
    var item = this.items[this.activeItemIndex];
    if (!item) return;

    var mediaItems = galleryMedia(item);
    var media = mediaItems[this.activeMediaIndex] || mediaItems[0];
    this.mediaContent.replaceChildren();

    if (media.kind === 'video') {
      var video = createElement('video', 'phase5-native-gallery__modal-media');
      video.src = media.url;
      if (media.poster) video.poster = media.poster;
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('aria-label', media.alt || item.caption.slice(0, 140));
      this.mediaContent.appendChild(video);
    } else {
      var image = createElement('img', 'phase5-native-gallery__modal-media');
      image.src = media.url;
      image.alt = media.alt || item.caption.slice(0, 140);
      image.decoding = 'async';
      image.draggable = false;
      this.mediaContent.appendChild(image);
    }

    setText(this.caption, item.caption);
    var date = safeDate(item.createdAt);
    setText(this.byline, '@' + item.username + (date ? ' · ' + date : ''));

    this.products.replaceChildren();
    (SHOPPABLE_LINKS_ENABLED ? item.productLinks : []).forEach(function (product) {
      var listItem = createElement('li');
      var link = createElement('a', 'phase5-native-gallery__product', product.title);
      link.href = product.url;
      link.dataset.shopifyProductId = product.shopifyProductId;
      listItem.appendChild(link);
      this.products.appendChild(listItem);
    }, this);
    this.products.hidden = !SHOPPABLE_LINKS_ENABLED || !item.productLinks.length;

    if (item.permalink) {
      this.instagramLink.href = item.permalink;
      this.instagramLink.hidden = false;
    } else {
      this.instagramLink.hidden = true;
    }

    this.renderMediaPagination(mediaItems.length);
  };

  NativeGallery.prototype.changeItem = function (direction) {
    if (!this.items[this.activeItemIndex] || !this.rendered) return;
    this.activeItemIndex = (this.activeItemIndex + direction + this.rendered) % this.rendered;
    this.activeMediaIndex = 0;
    this.renderModal();
  };

  NativeGallery.prototype.changeMedia = function (index, restoreFocus) {
    var item = this.items[this.activeItemIndex];
    if (!item) return;
    var mediaItems = galleryMedia(item);
    if (!Number.isInteger(index) || index < 0 || index >= mediaItems.length || index === this.activeMediaIndex) return;
    this.activeMediaIndex = index;
    this.renderModal();
    if (restoreFocus && this.mediaDots) {
      var activeDot = this.mediaDots.querySelector('[data-gallery-media-index="' + index + '"]');
      if (activeDot) activeDot.focus();
    }
  };

  NativeGallery.prototype.renderMediaPagination = function (mediaTotal) {
    var multipleMedia = mediaTotal > 1;
    if (this.mediaPagination) this.mediaPagination.hidden = !multipleMedia;
    if (this.mediaCount) {
      this.mediaCount.hidden = !multipleMedia;
      setText(this.mediaCount, multipleMedia ? (this.activeMediaIndex + 1) + ' / ' + mediaTotal : '');
    }
    if (!this.mediaDots) return;

    this.mediaDots.replaceChildren();
    this.mediaDots.hidden = !multipleMedia;
    if (!multipleMedia) return;

    var fragment = document.createDocumentFragment();
    for (var index = 0; index < mediaTotal; index += 1) {
      var dot = createElement('button', 'phase5-native-gallery__media-dot');
      dot.type = 'button';
      dot.dataset.galleryMediaIndex = String(index);
      dot.setAttribute('aria-label', 'Show slide ' + (index + 1) + ' of ' + mediaTotal);
      if (index === this.activeMediaIndex) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      }
      fragment.appendChild(dot);
    }
    this.mediaDots.appendChild(fragment);
  };

  NativeGallery.prototype.onMediaPointerDown = function (event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('button, a, video')) return;
    this.mediaPointerStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY
    };
  };

  NativeGallery.prototype.onMediaPointerUp = function (event) {
    if (!this.mediaPointerStart || this.mediaPointerStart.pointerId !== event.pointerId) return;
    var deltaX = event.clientX - this.mediaPointerStart.x;
    var deltaY = event.clientY - this.mediaPointerStart.y;
    this.mediaPointerStart = null;

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
    var item = this.items[this.activeItemIndex];
    var mediaItems = item ? galleryMedia(item) : [];
    if (mediaItems.length > 1) {
      var nextMedia = this.activeMediaIndex + (deltaX < 0 ? 1 : -1);
      if (nextMedia >= 0 && nextMedia < mediaItems.length) this.changeMedia(nextMedia, false);
    } else {
      this.changeItem(deltaX < 0 ? 1 : -1);
    }
  };

  NativeGallery.prototype.onMediaPointerCancel = function () {
    this.mediaPointerStart = null;
  };

  NativeGallery.prototype.scrollCarousel = function (direction) {
    if (!this.carouselViewport) return;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.carouselViewport.scrollBy({
      left: direction * this.carouselViewport.clientWidth,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
    window.setTimeout(this.updateCarouselControls, reduceMotion ? 0 : 350);
  };

  NativeGallery.prototype.updateCarouselControls = function () {
    if (!this.carouselViewport) return;
    var maximum = Math.max(0, this.carouselViewport.scrollWidth - this.carouselViewport.clientWidth);
    var position = Math.max(0, this.carouselViewport.scrollLeft);
    if (this.carouselPreviousButton) this.carouselPreviousButton.hidden = position <= 2;
    if (this.carouselNextButton) this.carouselNextButton.hidden = maximum <= 2 || position >= maximum - 2;
  };

  NativeGallery.prototype.openUpload = function (source) {
    if (!this.uploadDialog) return;
    setText(this.uploadStatus, this.uploadEndpoint ? '' : (this.root.dataset.uploadUnavailable || 'Uploads are not enabled on this staging preview yet.'));
    this.openModal(this.uploadDialog, source);
    if (this.turnstileSiteKey) this.ensureTurnstile();
  };

  NativeGallery.prototype.openModal = function (dialog, source) {
    if (!dialog) return;
    this.returnFocus = source || document.activeElement;
    this.openDialog = dialog;
    dialog.hidden = false;
    document.body.classList.add('phase5-native-gallery-open');
    var first = dialog.querySelector(FOCUSABLE_SELECTOR);
    if (first) window.setTimeout(function () { first.focus(); }, 0);
  };

  NativeGallery.prototype.closeActiveDialog = function () {
    if (!this.openDialog) return;
    var video = this.openDialog.querySelector('video');
    if (video) video.pause();
    this.openDialog.hidden = true;
    this.openDialog = null;
    document.body.classList.remove('phase5-native-gallery-open');
    if (this.returnFocus && typeof this.returnFocus.focus === 'function') this.returnFocus.focus();
    this.returnFocus = null;
  };

  NativeGallery.prototype.onKeydown = function (event) {
    if (!this.openDialog) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeActiveDialog();
      return;
    }

    var focusedMediaDot = event.target.closest && event.target.closest('[data-gallery-media-index]');
    if (this.openDialog === this.modal && focusedMediaDot && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      var direction = event.key === 'ArrowLeft' ? -1 : 1;
      var item = this.items[this.activeItemIndex];
      var mediaTotal = item ? galleryMedia(item).length : 0;
      var nextMedia = Math.min(Math.max(this.activeMediaIndex + direction, 0), Math.max(0, mediaTotal - 1));
      this.changeMedia(nextMedia, true);
      return;
    }

    if (this.openDialog === this.modal && event.key === 'ArrowLeft') {
      event.preventDefault();
      this.changeItem(-1);
      return;
    }

    if (this.openDialog === this.modal && event.key === 'ArrowRight') {
      event.preventDefault();
      this.changeItem(1);
      return;
    }

    if (event.key === 'Tab') {
      var focusable = Array.prototype.slice.call(this.openDialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  NativeGallery.prototype.onUploadSubmit = function (event) {
    event.preventDefault();
    if (!this.uploadForm || !this.uploadStatus) return;

    if (!this.uploadEndpoint || !isSafeHttpUrl(this.uploadEndpoint)) {
      setText(this.uploadStatus, this.root.dataset.uploadUnavailable || 'Uploads are not enabled on this staging preview yet.');
      return;
    }

    if (!this.uploadForm.checkValidity()) {
      this.uploadForm.reportValidity();
      return;
    }

    var fileInput = this.uploadForm.querySelector('[name="media"]');
    var file = fileInput && fileInput.files ? fileInput.files[0] : null;
    var accepted = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/quicktime'];

    if (!file || accepted.indexOf(file.type) === -1) {
      setText(this.uploadStatus, 'Choose a JPG, PNG, WebP, MP4, or MOV file.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setText(this.uploadStatus, 'The selected file is larger than 50 MB.');
      return;
    }

    var gallery = this;
    var submit = this.uploadForm.querySelector('[type="submit"]');
    var formData = new FormData(this.uploadForm);
    if (this.productHandle) formData.set('productHandle', this.productHandle);
    if (this.turnstileSiteKey && !formData.get('cf-turnstile-response')) {
      setText(this.uploadStatus, 'Please complete the verification before submitting.');
      return;
    }
    formData.append('source', 'phase5-native-gallery');
    formData.append('store', window.location.host);
    formData.append('submittedAt', new Date().toISOString());

    submit.disabled = true;
    setText(this.uploadStatus, 'Submitting your photo or video for review…');

    fetch(this.uploadEndpoint, {
      method: 'POST',
      body: formData,
      credentials: 'omit',
      mode: 'cors'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Upload returned ' + response.status);
        return response.json();
      })
      .then(function (result) {
        if (!result || result.ok !== true || result.state !== 'pending' || !result.submissionId) throw new Error('Upload was not acknowledged.');
        gallery.uploadForm.reset();
        setText(gallery.uploadStatus, gallery.root.dataset.uploadSuccess || 'Thanks! Your submission is pending review.');
      })
      .catch(function () {
        setText(gallery.uploadStatus, gallery.root.dataset.uploadError || 'Your upload could not be submitted. Please try again.');
      })
      .finally(function () {
        submit.disabled = false;
        var widget = gallery.root.querySelector('[data-gallery-turnstile]');
        if (window.turnstile && widget && widget.dataset.widgetId) window.turnstile.reset(widget.dataset.widgetId);
      });
  };

  NativeGallery.prototype.ensureTurnstile = function () {
    if (window.turnstile) {
      this.renderTurnstile();
      return;
    }

    var gallery = this;
    var existing = document.querySelector('script[data-phase5-turnstile]');
    if (existing) {
      existing.addEventListener('load', function () { gallery.renderTurnstile(); }, { once: true });
      return;
    }

    var script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.phase5Turnstile = 'true';
    script.addEventListener('load', function () { gallery.renderTurnstile(); }, { once: true });
    document.head.appendChild(script);
  };

  NativeGallery.prototype.renderTurnstile = function () {
    var container = this.root.querySelector('[data-gallery-turnstile]');
    if (!container || container.dataset.rendered || !window.turnstile) return;
    container.dataset.widgetId = window.turnstile.render(container, { sitekey: this.turnstileSiteKey, theme: 'light' });
    container.dataset.rendered = 'true';
  };

  NativeGallery.prototype.showStatus = function (message, visible) {
    if (!this.status) return;
    setText(this.status, message);
    this.status.hidden = !visible;
  };

  NativeGallery.prototype.hideSkeleton = function () {
    if (this.skeleton) this.skeleton.hidden = true;
  };

  NativeGallery.prototype.updateLoadMore = function () {
    if (!this.loadMoreButton) return;
    if (this.carouselMode) {
      this.loadMoreButton.hidden = true;
      return;
    }
    this.loadMoreButton.hidden = this.rendered >= this.items.length && !this.nextUrl;
  };

  function initialize(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    roots.forEach(function (root) {
      if (root.dataset.galleryInitialized === 'true') return;
      root.dataset.galleryInitialized = 'true';
      new NativeGallery(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialize(document); });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });
})();
